import type { CustomerFilters } from '../types/customer.types';

export const MINIMUM_BOOKING_VALUES = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '10',
  '15',
  '20',
  '30',
  '50',
] as const;

export type MinimumBookingValue = (typeof MINIMUM_BOOKING_VALUES)[number];

const MINIMUM_BOOKING_VALUE_SET = new Set<string>(MINIMUM_BOOKING_VALUES);

export const NUMBERS_OF_BOOKINGS_PARAM = 'numbersOfBookings';
export const MIN_EDGE_BOOKING_PERIOD_PARAM = 'minEdgeBookingPeriod';
export const MAX_EDGE_BOOKING_PERIOD_PARAM = 'maxEdgeBookingPeriod';
export const FORM_CODE_PARAM = 'formCode';

export const DEFAULT_CUSTOMER_FILTERS: CustomerFilters = {
  tags: [],
  yearsOfBirth: [],
  months: [],
  minimumBookings: '',
  minEdgeBookingPeriod: '',
  maxEdgeBookingPeriod: '',
  includeMail: false,
  includeCustomersWithMarketingConsent: false,
  isInBlackList: false,
  phonePrefix: '',
  formCode: '',
};

const FILTER_ARRAY_KEYS = {
  tags: 'tags[]',
  yearsOfBirth: 'yearsOfBirth[]',
  months: 'month[]',
} as const;

const FILTER_BOOLEAN_KEYS = [
  'includeMail',
  'includeCustomersWithMarketingConsent',
  'isInBlackList',
] as const;

const PHONE_PREFIX_PARAM = 'prefix';

function getAllFilterParamNames(): string[] {
  return [
    'marketingStatus',
    ...Object.values(FILTER_ARRAY_KEYS),
    ...FILTER_BOOLEAN_KEYS,
    PHONE_PREFIX_PARAM,
    'prefix39Only',
    NUMBERS_OF_BOOKINGS_PARAM,
    MIN_EDGE_BOOKING_PERIOD_PARAM,
    MAX_EDGE_BOOKING_PERIOD_PARAM,
    FORM_CODE_PARAM,
  ];
}

function appendUniqueValues(
  params: URLSearchParams,
  key: string,
  values: string[],
): void {
  const unique = [
    ...new Set(values.map((value) => value.trim()).filter(Boolean)),
  ];
  unique.sort().forEach((value) => {
    params.append(key, value);
  });
}

function parseMinimumBookings(searchParams: URLSearchParams): string {
  const value = searchParams.get(NUMBERS_OF_BOOKINGS_PARAM)?.trim() ?? '';
  return MINIMUM_BOOKING_VALUE_SET.has(value) ? value : '';
}

export function parseCustomerFiltersFromSearchParams(
  searchParams: URLSearchParams,
): CustomerFilters {
  const legacyPrefix39 = searchParams.get('prefix39Only') === 'true';
  const rawPrefix = searchParams.get(PHONE_PREFIX_PARAM)?.trim() ?? '';
  const phonePrefix = /^\d{1,4}$/.test(rawPrefix)
    ? rawPrefix
    : legacyPrefix39
      ? '39'
      : '';

  const excludedParams = new URLSearchParams();
  searchParams.forEach((value, key) => {
    const match = /^exclude\[([^\]]+)\](\[\])?$/.exec(key);
    if (match) excludedParams.append(match[1] + (key.endsWith('[]') ? '[]' : ''), value);
  });
  const exclude = excludedParams.size
    ? {
        ...parseCustomerFiltersFromSearchParams(excludedParams),
        search: excludedParams.get('search') ?? '',
      }
    : undefined;
  return {
    exclude,
    marketingStatus: searchParams.get('marketingStatus') ?? '',
    tags: searchParams.getAll('tags[]'),
    yearsOfBirth: searchParams.getAll('yearsOfBirth[]'),
    months: searchParams
      .getAll('month[]')
      .filter((month) => /^(?:[1-9]|1[0-2])$/.test(month)),
    minimumBookings: parseMinimumBookings(searchParams),
    minEdgeBookingPeriod:
      searchParams.get(MIN_EDGE_BOOKING_PERIOD_PARAM)?.trim() ?? '',
    maxEdgeBookingPeriod:
      searchParams.get(MAX_EDGE_BOOKING_PERIOD_PARAM)?.trim() ?? '',
    includeMail: searchParams.get('includeMail') === 'true',
    includeCustomersWithMarketingConsent:
      searchParams.get('includeCustomersWithMarketingConsent') === 'true',
    isInBlackList: searchParams.get('isInBlackList') === 'true',
    phonePrefix,
    formCode: searchParams.get(FORM_CODE_PARAM)?.trim() ?? '',
  };
}

export function applyCustomerFiltersToSearchParams(
  params: URLSearchParams,
  filters: CustomerFilters,
): void {
  appendCustomerExclusions(params, filters.exclude);
  getAllFilterParamNames().forEach((key) => {
    params.delete(key);
  });

  appendUniqueValues(params, FILTER_ARRAY_KEYS.tags, filters.tags);
  appendUniqueValues(
    params,
    FILTER_ARRAY_KEYS.yearsOfBirth,
    filters.yearsOfBirth,
  );
  appendUniqueValues(params, FILTER_ARRAY_KEYS.months, filters.months);

  if (filters.marketingStatus)
    params.set('marketingStatus', filters.marketingStatus);

  if (filters.minimumBookings) {
    params.set(NUMBERS_OF_BOOKINGS_PARAM, filters.minimumBookings);
  }

  if (filters.minEdgeBookingPeriod) {
    params.set(MIN_EDGE_BOOKING_PERIOD_PARAM, filters.minEdgeBookingPeriod);
  }

  if (filters.maxEdgeBookingPeriod) {
    params.set(MAX_EDGE_BOOKING_PERIOD_PARAM, filters.maxEdgeBookingPeriod);
  }

  if (filters.includeMail) {
    params.set('includeMail', 'true');
  }
  if (filters.includeCustomersWithMarketingConsent) {
    params.set('includeCustomersWithMarketingConsent', 'true');
  }
  if (filters.isInBlackList) {
    params.set('isInBlackList', 'true');
  }
  if (filters.phonePrefix) {
    params.set(PHONE_PREFIX_PARAM, filters.phonePrefix);
  }
  if (filters.formCode) {
    params.set(FORM_CODE_PARAM, filters.formCode);
  }
}

export function buildCustomerFiltersCacheKey(filters: CustomerFilters): string {
  return [
    filters.marketingStatus ?? '',
    [...filters.tags].sort().join(','),
    [...filters.yearsOfBirth].sort().join(','),
    [...filters.months].sort().join(','),
    filters.minimumBookings,
    filters.minEdgeBookingPeriod,
    filters.maxEdgeBookingPeriod,
    String(filters.includeMail),
    String(filters.includeCustomersWithMarketingConsent),
    String(filters.isInBlackList),
    filters.phonePrefix,
    filters.formCode,
    customerExclusionsKey(filters.exclude),
  ].join('|');
}

export function hasActiveCustomerFilters(filters: CustomerFilters): boolean {
  return (
    Boolean(customerExclusionsKey(filters.exclude)) ||
    Boolean(filters.marketingStatus) ||
    filters.tags.length > 0 ||
    filters.yearsOfBirth.length > 0 ||
    filters.months.length > 0 ||
    filters.minimumBookings !== '' ||
    filters.minEdgeBookingPeriod !== '' ||
    filters.maxEdgeBookingPeriod !== '' ||
    filters.includeMail ||
    filters.includeCustomersWithMarketingConsent ||
    filters.isInBlackList ||
    filters.phonePrefix !== '' ||
    filters.formCode !== ''
  );
}

export const BIRTH_YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  const oldestYear = 1940;
  return Array.from({ length: currentYear - oldestYear + 1 }, (_, index) =>
    String(currentYear - index),
  );
})();

export const BIRTH_MONTH_VALUES = Array.from({ length: 12 }, (_, index) =>
  String(index + 1),
);

export function appendCustomerExclusions(
  params: URLSearchParams,
  exclude?: CustomerFilters['exclude'],
): void {
  [...params.keys()]
    .filter((key) => key.startsWith('exclude['))
    .forEach((key) => { params.delete(key); });
  if (!exclude) return;
  const nested = new URLSearchParams();
  applyCustomerFiltersToSearchParams(nested, {
    ...DEFAULT_CUSTOMER_FILTERS,
    ...exclude,
    exclude: undefined,
  });
  if (exclude.search?.trim()) nested.set('search', exclude.search.trim());
  nested.forEach((value, key) => {
    const array = key.endsWith('[]');
    params.append(
      `exclude[${array ? key.slice(0, -2) : key}]${array ? '[]' : ''}`,
      value,
    );
  });
}
function customerExclusionsKey(exclude?: CustomerFilters['exclude']): string {
  const params = new URLSearchParams();
  appendCustomerExclusions(params, exclude);
  return params.toString();
}
